import {
  Checkbox,
  Form,
  FormGroup,
  FormLabel,
  NumberInput,
  Select,
  SelectItem,
} from '@carbon/react';

import React, { useState, useEffect } from 'react';
import { usePrevious } from '../Helpers';

import './_scheduler.scss';

const defaultSettings = {
  begin: '8:0',
  end: '23:59',
  fullDay: true,
  weeklySelection: [],
  monthlySelection: [],
  intervalType: 'oneshot',
  intervalSetting: [5, 'minutes'],
};

const encodeCron = (settings_args) => {
  let settings = { ...defaultSettings, ...settings_args };

  // second
  let cron_template = ' * ';

  const [begin_hours, begin_minutes] = settings.begin.split(':');
  //const [end_hours, end_minutes] = settings.end.split(":");
  const [interval_value, interval_type] =
    settings.intervalType === 'repeatly'
      ? settings.intervalSetting
      : ['', 'oneshot'];

  if (interval_type === 'minutes' && interval_value > 0) {
    cron_template += `0/${interval_value}`;
  } else {
    if (interval_type === 'oneshot') {
      cron_template += `${begin_minutes || 0}`;
    } else {
      cron_template += '*';
    }
  }
  cron_template += ' ';

  if (interval_type === 'hours' && interval_value > 0) {
    cron_template += `0/${interval_value}`;
  } else {
    if (interval_type === 'oneshot') {
      cron_template += `${begin_hours || 0}`;
    } else {
      cron_template += '*';
    }
  }

  cron_template += ' ';

  // plan ist monthly
  let cron_monthly =
    settings.monthlySelection.length > 0
      ? settings.monthlySelection.join(',')
      : '*';
  cron_template += cron_monthly;

  cron_template += ' ';

  // month (May-Aug)
  cron_template += '* ';

  // plan ist weekly
  let cron_weekdays = settings.weeklySelection
    .map((s) => {
      switch (s) {
        case 'mo':
          return 'MON';
        case 'di':
          return 'TUE';
        case 'mi':
          return 'WED';
        case 'do':
          return 'THU';
        case 'fr':
          return 'FRI';
        case 'sa':
          return 'SAT';
        default:
          return 'SUN';
      }
    })
    .join(',');

  cron_template += settings.weeklySelection.length > 0 ? cron_weekdays : '*';

  cron_template += ' ';

  // year (2018/2)
  cron_template += '* ';

  cron_template += '|';
  cron_template += `${settings.begin || '0:0'}`;

  if (!settings.fullDay || settings.fullDay === false) {
    cron_template += `-${settings.end || '23:59'}`;
  }

  cron_template += '|' + JSON.stringify(settings);

  return cron_template;
};

const decodeCron = (expression = '') => {
  if (expression === '') {
    return {};
  }

  const parts = expression.split('|');
  if (parts.length < 3) {
    // contains settings object
    return {};
  }

  return JSON.parse(parts[2]);
};

/// custom Hook, to work with items with an Array as Values
const useArrayState = (initialValue, onChange = (_e, _a) => {}) => {
  const [state, setSelectedDays] = useState(initialValue || []);

  const changeField = (no, value) => {
    const ind = state.findIndex((d) => d === no);

    if (value === true) {
      if (ind < 0) {
        let f = [...state];
        f.push(no);
        onChange(f);
      }
    } else {
      if (ind > -1) {
        onChange(state.filter((v, _i, _a) => v !== no));
      }
    }
  };

  const changeInitialValue = (value) => {
    setSelectedDays(value || []);
  };

  return [state, changeField, changeInitialValue];
};

/// custom Hook, to implement directly the callback method
const useStateWithHandler = (
  initialValue,
  get_raw_value = (e) => {
    return e;
  }
) => {
  const [state, setState] = useState(initialValue);
  const onHandleChange = (e) => {
    setState(get_raw_value(e));
  };

  return [state, setState, onHandleChange];
};

const CustomCheckBox = ({
  labelText,
  id,
  fieldValue,
  currentState,
  onChange,
}) => {
  return (
    <Checkbox
      labelText={labelText}
      id={id}
      checked={currentState.includes(fieldValue)}
      onChange={(_, {checked}) => onChange(fieldValue, checked)}
    />
  );
  //return
};

const WeeklyScheduler = ({ onChange, value }) => {
  const [currentState, changeField, changeState] = useArrayState(
    value,
    onChange
  );

  useEffect(() => {
    changeState(value);
  }, [value, changeState]);

  return (
    <>
      <FormLabel>Wochentage</FormLabel>
      <br />
      <div className="weekly_list">
        <CustomCheckBox
          labelText="Mo"
          id="day_mo"
          fieldValue="mo"
          currentState={currentState}
          onChange={changeField}
        />
        <CustomCheckBox
          labelText="Di"
          id="day_di"
          fieldValue="di"
          currentState={currentState}
          onChange={changeField}
        />
        <CustomCheckBox
          labelText="Mi"
          id="day_mi"
          fieldValue="mi"
          currentState={currentState}
          onChange={changeField}
        />
        <CustomCheckBox
          labelText="Do"
          id="day_do"
          fieldValue="do"
          currentState={currentState}
          onChange={changeField}
        />
        <CustomCheckBox
          labelText="Fr"
          id="day_fr"
          fieldValue="fr"
          currentState={currentState}
          onChange={changeField}
        />
        <CustomCheckBox
          labelText="Sa"
          id="day_sa"
          fieldValue="sa"
          currentState={currentState}
          onChange={changeField}
        />
        <CustomCheckBox
          labelText="So"
          id="day_so"
          fieldValue="so"
          currentState={currentState}
          onChange={changeField}
        />
      </div>
    </>
  );
};

const MonthlyScheduler = ({ onChange, value }) => {
  const [selected_days, changeDay, changeState] = useArrayState(
    value,
    onChange
  );

  useEffect(() => {
    changeState(value);
  }, [value, changeState]);

  return (
    <>
      <FormLabel>Monatlich</FormLabel>
      <br />
      <div className="monthly_list">
        {[...Array(7)].map((e, i) => (
          <CustomCheckBox
            key={`day_${1 + i}`}
            labelText={`${1 + i}. `}
            id={`day_${1 + i}`}
            fieldValue={1 + i}
            currentState={selected_days}
            onChange={changeDay}
          />
        ))}
      </div>
      <div className="monthly_list">
        {[...Array(7)].map((e, i) => (
          <CustomCheckBox
            key={`day_${8 + i}`}
            labelText={`${8 + i}. `}
            id={`day_${8 + i}`}
            fieldValue={8 + i}
            currentState={selected_days}
            onChange={changeDay}
          />
        ))}
      </div>
      <div className="monthly_list">
        {[...Array(7)].map((e, i) => (
          <CustomCheckBox
            key={`day_${15 + i}`}
            labelText={`${15 + i}. `}
            id={`day_${15 + i}`}
            fieldValue={15 + i}
            currentState={selected_days}
            onChange={changeDay}
          />
        ))}
      </div>
      <div className="monthly_list">
        {[...Array(7)].map((e, i) => (
          <CustomCheckBox
            key={`day_${22 + i}`}
            labelText={`${22 + i}. `}
            id={`day_${22 + i}`}
            fieldValue={22 + i}
            currentState={selected_days}
            onChange={changeDay}
          />
        ))}
      </div>

      <div className="monthly_list">
        {[...Array(3)].map((e, i) => (
          <CustomCheckBox
            key={`day_${29 + i}`}
            labelText={`${29 + i}. `}
            id={`day_${29 + i}`}
            fieldValue={29 + i}
            currentState={selected_days}
            onChange={changeDay}
          />
        ))}
      </div>
    </>
  );
};

const IntervalSelection = ({ value, onChange = (_value) => {} }) => {
  const [timeValue, setTimeValue] = useState(0);
  const [intervalTypeSelection, setIntervalTypeSelection] = useState();

  const changeTimeValue = (value, direction) => {
    let currentValue = timeValue;
    if (value > 0) {
      currentValue = parseInt(value);
    } else {
      if (direction === 'up') {
        currentValue += 1;
      } else {
        if (direction === 'down') {
          currentValue -= 1;
        }
      }
    }
    
    if (intervalTypeSelection === 'hours') {
      if (currentValue > 23) {
        currentValue = 23;
      }
    } else {
      if (currentValue > 59) {
        currentValue = 59;
      }
    }

    if (currentValue < 0) {
      currentValue = 0;
    }

    if (currentValue !== timeValue) {
      onChange([currentValue, intervalTypeSelection]);
    }
  };

  const changeIntervalType = (changedSelection) => {
    let currentValue = timeValue;

    if (changedSelection.target.value === 'hours') {
      if (currentValue > 23) {
        currentValue = 23;
      }
    } else {
      if (currentValue > 59) {
        currentValue = 59;
      }
    }

    if (currentValue < 0) {
      currentValue = 0;
    }

    onChange([currentValue, changedSelection.target.value]);
  };

  useEffect(() => {
    let currentValue = value[0];
    if (value[1] === 'hours') {
      if (currentValue > 23) {
        currentValue = 23;
      }
    } else {
      if (currentValue > 59) {
        currentValue = 59;
      }
    }

    if (currentValue < 0) {
      currentValue = 0;
    }

    setTimeValue(currentValue);

    if (value[1] !== intervalTypeSelection) {
      setIntervalTypeSelection(value[1]);
    }
  }, [value, intervalTypeSelection]);

  return (
    <div className="time_container-outer">
      <FormLabel>Zeit zwischen der Einblendung</FormLabel>
      <br />

      <div className="time_container">
        <NumberInput
          id="number_field_interval"
          size="sm"
          onChange={(_e, {value,direction}) => changeTimeValue(value, direction)}
          value={timeValue}
          max={intervalTypeSelection === 'minutes' ? 59 : 23}
          className="number_time_input"
        />
        <Select
          size="sm"
          hideLabel
          id="selection_choose_interval_weight"
          value={intervalTypeSelection}
          onChange={changeIntervalType}>
          <SelectItem value="minutes" text="Minuten" />
          <SelectItem value="hours" text="Stunden" />
        </Select>
      </div>
    </div>
  );
};

const HourMinute = ({ id, value, onChange = (_t) => {} }) => {
  let preValue = [0, 0];
  if (value && typeof value === 'string') {
    const t = value.split(':');
    preValue = [parseInt(t[0]), parseInt(t[1])];
  }

  const [timeBegin, setTimeBegin] = useState(preValue);
  const changeTimeBegin = (index, value, direction) => {
    let currentValue = timeBegin[index];
    
    if (value > 0) {
      currentValue = parseInt(value);
    } else {
      if (direction === 'up') {
        currentValue += 1;
      } else {
        if (direction === 'down') {
          currentValue -= 1;
        }
      }
    }
   
    if (index === 0) {
      if (currentValue > 23) {
        currentValue = 23;
      }
    } else {
      if (currentValue > 59) {
        currentValue = 59;
      }
    }

    if (currentValue < 0) {
      currentValue = 0;
    }

    let t2 = timeBegin;
    t2[index] = currentValue;
    //setTimeBegin(t2);

    onChange(`${t2[0]}:${t2[1]}`);
  };

  useEffect(() => {
    if (value && typeof value === 'string') {
      const t = value.split(':');
      setTimeBegin([parseInt(t[0]), parseInt(t[1])]);
    }
  }, [value]);

  return (
    <div id={id} className="time_container">
      <NumberInput
        id={`${id}_hour`}
        size="sm"
        max={23}
        min={0}
        value={timeBegin[0]}
        onChange={(_e, {value,direction}) => changeTimeBegin(0,value, direction)}
        className="number_time_input"
      />
      <div className="separator">:</div>
      <NumberInput
        id={`${id}_minute`}
        size="sm"
        max={59}
        min={0}
        value={timeBegin[1]}
        onChange={(_e, {value,direction}) => changeTimeBegin(1,value, direction)}
        className="number_time_input"
      />
    </div>
  );
};

export const Scheduler = ({ onChange, value }) => {
  const propsValue = { ...defaultSettings, ...decodeCron(value || '') };

  const [
    intervalType,
    setIntervalType,
    change_interval_type_selection,
  ] = useStateWithHandler(propsValue.intervalType, (e) => e.target.value);
  const [
    intervalSetting,
    setIntervalSetting,
    change_interval_settings,
  ] = useStateWithHandler(propsValue.intervalSetting);
  const [full_day, setFullDay, change_full_day] = useStateWithHandler(
    propsValue.fullDay
  );
  const [
    monthlySelection,
    setMonthlySelection,
    change_monthly_selection,
  ] = useStateWithHandler(propsValue.monthlySelection);
  const [
    weeklySelection,
    setWeeklySelection,
    change_weekly_selection,
  ] = useStateWithHandler(propsValue.weeklySelection);

  const [timeBegin, setTimeBegin, changeTimeBegin] = useStateWithHandler(
    propsValue.begin
  );
  const [timeEnd, setTimeEnd, changeTimeEnd] = useStateWithHandler(
    propsValue.end
  );

  const prevValue = usePrevious(value);
  const [cron, setCron] = useState(propsValue);

  useEffect(() => {
    const settings = decodeCron(value || '');
    setCron(settings);

    if (!prevValue && value) {
      console.info("prevValie ost not set, but now");
      setMonthlySelection(settings.monthlySelection);
      setWeeklySelection(settings.weeklySelection);
      setIntervalType(settings.intervalType);
      setFullDay(settings.full_day);
      setIntervalSetting(settings.intervalSetting);
      setTimeBegin(settings.begin);
      setTimeEnd(settings.end);
    }
  }, [
    value,
    prevValue,
    setFullDay,
    setIntervalSetting,
    setIntervalType,
    setMonthlySelection,
    setTimeBegin,
    setTimeEnd,
    setWeeklySelection,
  ]);

  // effect gets triggered if anything from this elements are changed
  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    const newCron = encodeCron({
      ...cron,
      intervalType,
      weeklySelection,
      monthlySelection,
      begin: timeBegin,
      end: timeEnd,
      fullDay: full_day,
      intervalSetting,
    });
    if (value !== undefined && value !== newCron) {
      onChange(newCron);
    }
  }, [
    cron,
    //onChange,
    //value,
    intervalType,
    weeklySelection,
    monthlySelection,
    timeBegin,
    timeEnd,
    full_day,
    intervalSetting,
  ]);

  return (
    <Form className="scheduler-form">
      <FormGroup
        legendText="An welchen Tagen?"
      >
        <p className="scheduler-hint">
          Wähle Wochentage und/oder feste Tage im Monat. Bleibt alles leer,
          läuft das Element an jedem Tag.
        </p>
        <WeeklyScheduler
          onChange={change_weekly_selection}
          value={weeklySelection}
        />
        <MonthlyScheduler
          onChange={change_monthly_selection}
          value={monthlySelection}
        />
      </FormGroup>

      <FormGroup
        legendText="Zu welcher Uhrzeit?"
      >
        <p className="scheduler-hint">
          „Ab" ist der Zeitpunkt, an dem das Element frühestens eingeblendet
          wird. „Bis" begrenzt das Ende des Anzeige-Fensters — oder „Ganztägig",
          dann ist nur „Ab" relevant.
        </p>
        <div className="scheduler-time-row">
          <div className="time_container-outer">
            <FormLabel>Ab</FormLabel>
            <HourMinute
              id="time_begin"
              value={timeBegin}
              onChange={changeTimeBegin}
            />
          </div>
          {full_day === false && (
            <div className="time_container-outer">
              <FormLabel>Bis</FormLabel>
              <HourMinute
                id="time_end"
                value={timeEnd}
                onChange={changeTimeEnd}
                onlyHour
              />
            </div>
          )}
          <div className="scheduler-fullday">
            <Checkbox
              labelText="Ganztägig"
              defaultChecked={full_day}
              onChange={change_full_day}
              id="enable_fullday"
            />
          </div>
        </div>
      </FormGroup>

      <FormGroup
        legendText="Wie oft?"
      >
        <p className="scheduler-hint">
          „Einmalig" zeigt das Element pro Tag genau einmal zum „Ab"-Zeitpunkt.
          „Wiederholend" zeigt es während des Zeitfensters immer wieder im
          gewählten Abstand (z. B. alle 5 Minuten).
        </p>
        <Select
          size="sm"
          hideLabel
          value={intervalType}
          id="interval_type_selection"
          labelText=""
          onChange={change_interval_type_selection}>
          <SelectItem value="oneshot" text="Einmalig" />
          <SelectItem value="repeatly" text="Wiederholend" />
        </Select>

        {intervalType === 'repeatly' && (
          <IntervalSelection
            value={intervalSetting}
            onChange={change_interval_settings}
            className="number_time_input"
          />
        )}
      </FormGroup>
    </Form>
  );
};
